// components/DriversTable.js
import React from 'react';
import DataTable from 'react-data-table-component';
import i18n from '../../../../../i18n';
import { Link } from 'react-router-dom';
import { Eye, Check, X, Edit, Trash2, User } from 'lucide-react';
import { tableStyles } from '../../../../../styles/components/js/tableStyle';

export class DriversTable extends React.Component {
    getColumns() {
        const { onApprove, onReject, onDelete, userRole } = this.props;
        
        const statusMap = {
            'A': { label: 'Active', color: '#10b981' },
            'P': { label: 'Pending', color: '#f59e0b' },
            'R': { label: 'Rejected', color: '#ef4444' }
        };

        const opalMap = {
            true: { label: 'OPAL', color: '#3b82f6', bgColor: '#dbeafe' },
            false: { label: 'Non-OPAL', color: '#8b5cf6', bgColor: '#f3e8ff' }
        };

        return [
            {
                name: 'ID',
                selector: row => row.id,
                sortable: true,
                width: '80px'
            },
            {
                name: 'Driver',
                cell: row => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {row.photo ? (
                            <img 
                                src={row.photo} 
                                alt={row.name}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                background: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <User size={20} color="#6b7280" />
                            </div>
                        )}
                        <div>
                            <div style={{ fontWeight: '500' }}>{row.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.phone}</div>
                        </div>
                    </div>
                ),
                sortable: true,
                sortFunction: (a, b) => a.name.localeCompare(b.name)
            },
            {
                name: 'License',
                selector: row => row.license_number,
                sortable: true,
                width: '150px'
            },
            {
                name: 'Type',
                cell: row => {
                    const type = opalMap[row.opal] || { label: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6' };
                    return (
                        <span
                            style={{
                                backgroundColor: type.bgColor,
                                color: type.color,
                                padding: '4px 12px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}
                        >
                            {type.label}
                        </span>
                    );
                },
                width: '120px'
            },
            {
                name: 'Vehicle',
                cell: row => (
                    <div>
                        {row.vehicle ? (
                            <div>
                                <div style={{ fontWeight: '500' }}>{row.vehicle.vehicle_number}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                    {row.vehicle.vehicle_model} • {row.vehicle.vehicle_type}
                                </div>
                            </div>
                        ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not assigned</span>
                        )}
                    </div>
                )
            },
            {
                name: 'Status',
                cell: row => {
                    const status = statusMap[row.status] || { label: row.status, color: '#6b7280' };
                    return (
                        <span
                            style={{
                                backgroundColor: `${status.color}15`,
                                color: status.color,
                                padding: '4px 12px',
                                borderRadius: '0.3rem',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}
                        >
                            {status.label}
                        </span>
                    );
                },
                sortable: true,
                width: '120px'
            },
            {
                name: 'Actions',
                width: '180px',
                center: true,
                cell: (row) => (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <Link
                            className="btn-new btn-sm"
                            to={`/drivers/${row.id}/edit`}
                            onClick={(e) => e.stopPropagation()}
                            title="View Details"
                        >
                            <Eye size={14} />
                        </Link>
                        
                        <Link
                            className="btn-new btn-sm"
                            to={`/drivers/${row.id}/edit`}
                            onClick={(e) => e.stopPropagation()}
                            title="Edit"
                        >
                            <Edit size={14} />
                        </Link>
                        
                        {row.status === 'P' && userRole === 'admin' && (
                            <>
                                <button
                                    className="btn-new btn-sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onApprove && onApprove(row);
                                    }}
                                    title="Approve"
                                    style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                                >
                                    <Check size={14} />
                                </button>
                                
                                <button
                                    className="btn-new btn-sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onReject && onReject(row);
                                    }}
                                    title="Reject"
                                    style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}
                                >
                                    <X size={14} />
                                </button>
                            </>
                        )}
                        
                        {userRole === 'admin' && (
                            <button
                                className="btn-new btn-sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Delete driver ${row.name}?`)) {
                                        onDelete && onDelete(row);
                                    }
                                }}
                                title="Delete"
                                style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}
                            >
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ),
            },
        ];
    }

    handleRowClick = (row) => {
        if (this.props.history) {
            this.props.history.push(`/drivers/${row.id}/edit`);
        }
    };

    render() {
        const { drivers, currentPage, limit, total, onChangePage, onChangeRowsPerPage } = this.props;

        const customTableStyles = {
            ...tableStyles,
            rows: {
                ...tableStyles.rows,
                style: {
                    ...tableStyles.rows?.style,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: '#f8f9fa',
                    },
                },
            },
        };

        return (
            <DataTable
                key={`${currentPage}-${limit}`}
                title=""
                columns={this.getColumns()}
                data={drivers}
                customStyles={customTableStyles}
                highlightOnHover
                pagination
                paginationServer
                paginationTotalRows={total}
                paginationPerPage={limit}
                paginationDefaultPage={currentPage}
                paginationRowsPerPageOptions={[15, 25, 50, 100]}
                paginationComponentOptions={{
                    rowsPerPageText: i18n.t('rows_per_page'),
                    rangeSeparatorText: i18n.t('out'),
                }}
                onChangeRowsPerPage={onChangeRowsPerPage}
                onChangePage={onChangePage}
                onRowClicked={this.handleRowClick}
            />
        );
    }
}